import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useCallback, useEffect } from "react";

interface GCalConnection {
  id: string;
  google_email: string;
  sync_enabled: boolean;
  last_sync_at: string | null;
  created_at: string;
}

export const useGoogleCalendarStatus = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["gcal-status"],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) throw new Error("No session");

      // functions.invoke doesn't support query params well, so use fetch
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gcal-auth?action=status`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
        }
      );
      if (!res.ok) throw new Error("Failed to check status");
      return (await res.json()) as {
        connected: boolean;
        connection: GCalConnection | null;
      };
    },
    enabled: !!user,
    staleTime: 30_000,
  });
};

export const useGoogleCalendarConnect = () => {
  const qc = useQueryClient();

  // Listen for popup message
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type === "gcal-connected") {
        qc.invalidateQueries({ queryKey: ["gcal-status"] });
        qc.invalidateQueries({ queryKey: ["calendar-events"] });
        toast.success(`Google Calendar connected (${e.data.email})`);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [qc]);

  const connect = useCallback(async () => {
    const { data: session } = await supabase.auth.getSession();
    const token = session?.session?.access_token;
    if (!token) {
      toast.error("Please sign in first");
      return;
    }

    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gcal-auth?action=authorize`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
      }
    );

    if (!res.ok) {
      const errBody = await res.text();
      console.error("gcal-auth authorize failed:", res.status, errBody);
      toast.error("Failed to start Google Calendar connection");
      return;
    }

    const { url } = await res.json();
    // Open popup
    const popup = window.open(url, "gcal-auth", "width=500,height=700,popup=yes");
    if (!popup) {
      // Fallback to redirect
      window.location.href = url;
    }
  }, []);

  return { connect };
};

export const useGoogleCalendarDisconnect = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) throw new Error("No session");

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gcal-auth?action=disconnect`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error("Failed to disconnect");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gcal-status"] });
      qc.invalidateQueries({ queryKey: ["calendar-events"] });
      toast.success("Google Calendar disconnected");
    },
    onError: (err: any) => toast.error(err.message || "Failed to disconnect"),
  });
};

export const useGoogleCalendarToggleSync = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) throw new Error("No session");

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gcal-auth?action=toggle-sync`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ enabled }),
        }
      );
      if (!res.ok) throw new Error("Failed to toggle sync");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gcal-status"] });
    },
    onError: (err: any) => toast.error(err.message || "Failed to toggle sync"),
  });
};

export const useGoogleCalendarSync = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (action: "push" | "pull" | "full-sync" = "full-sync") => {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) throw new Error("No session");

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gcal-sync`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ action }),
        }
      );
      if (!res.ok) throw new Error("Sync failed");
      return res.json();
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["calendar-events"] });
      const parts = [];
      if (data.created) parts.push(`${data.created} created`);
      if (data.updated) parts.push(`${data.updated} updated`);
      if (data.deleted) parts.push(`${data.deleted} deleted`);
      if (data.pushed) parts.push(`${data.pushed} pushed`);
      toast.success(parts.length ? `Sync complete: ${parts.join(", ")}` : "Sync complete — no changes");
    },
    onError: (err: any) => toast.error(err.message || "Sync failed"),
  });
};
